// Password rules shared by the sign-up form (live checklist) and anything else that needs them.
// Mirror these in Firebase Console -> Authentication -> Settings -> Password policy
// (Require: min 10, uppercase, lowercase, number, special character) so the SERVER enforces
// them too - client-side checks alone can be bypassed by calling Firebase's REST API directly.

export const PASSWORD_MIN = 10
export const PASSWORD_MAX = 128

// Small list of the most-guessed passwords. Not exhaustive - it only exists to stop the
// obvious ones that would otherwise pass the "upper + lower + number + symbol" checklist.
const COMMON = new Set([
  'password', 'password1', 'password123', 'passw0rd', 'p@ssw0rd', 'p@ssword1', 'qwerty', 'qwerty123',
  'qwertyuiop', 'qwerty@123', '123456', '1234567', '12345678', '123456789', '1234567890', '111111',
  '000000', 'abc123', 'abcd1234', 'iloveyou', 'welcome', 'welcome1', 'welcome@123', 'admin', 'admin123',
  'letmein', 'monkey', 'dragon', 'football', 'baseball', 'superman', 'batman', 'naruto', 'sasuke',
  'goku', 'anime', 'animora', 'animora123', 'animora@123', 'india123', 'india@123', 'test1234',
  'changeme', 'default', 'login', 'master', 'shadow', 'sunshine', 'princess', 'trustno1',
])

export type PasswordRules = {
  length: boolean
  lower: boolean
  upper: boolean
  number: boolean
  symbol: boolean
  notCommon: boolean
  notEmail: boolean
}

export function checkPassword(pw: string, email = ''): { ok: boolean; rules: PasswordRules; score: 0 | 1 | 2 | 3 | 4 } {
  const local = email.trim().toLowerCase().split('@')[0] || ''
  const lower = pw.toLowerCase()
  const squashed = lower.replace(/[^a-z0-9]/g, '')
  const rules: PasswordRules = {
    length: pw.length >= PASSWORD_MIN && pw.length <= PASSWORD_MAX,
    lower: /[a-z]/.test(pw),
    upper: /[A-Z]/.test(pw),
    number: /\d/.test(pw),
    symbol: /[^A-Za-z0-9\s]/.test(pw),
    notCommon: !COMMON.has(lower) && !COMMON.has(squashed) && !/^(.)\1+$/.test(pw) && !/^(0123|1234|2345|abcd|qwer)/i.test(pw),
    notEmail: local.length < 4 || (!lower.includes(local) && !squashed.includes(local.replace(/[^a-z0-9]/g, ''))),
  }
  const ok = Object.values(rules).every(Boolean)
  const passed = [rules.length, rules.lower && rules.upper, rules.number, rules.symbol].filter(Boolean).length
  let score = (rules.notCommon && rules.notEmail ? passed : Math.min(passed, 1)) as 0 | 1 | 2 | 3 | 4
  if (pw.length === 0) score = 0
  if (ok && pw.length >= 14) score = 4
  else if (ok) score = Math.max(score, 3) as 3 | 4
  return { ok, rules, score }
}

export const PASSWORD_RULE_LABELS: { key: keyof PasswordRules; label: string }[] = [
  { key: 'length', label: `${PASSWORD_MIN}+ characters` },
  { key: 'upper', label: 'Uppercase letter' },
  { key: 'lower', label: 'Lowercase letter' },
  { key: 'number', label: 'Number' },
  { key: 'symbol', label: 'Symbol (e.g. ! @ # $)' },
  { key: 'notCommon', label: 'Not a common password' },
  { key: 'notEmail', label: "Doesn't contain your email" },
]
